import { ProductHelper } from 'Helpers';
import Enumerable from 'linq';
import { ProduitDto } from 'Models';
import { MapHelper } from 'Plugins/FloorPlan/Helpers/MapHelper';
import { QuoteState } from 'Web/Pages/Quote/QuoteState';

import {
    OptionDto,
    PrestationDto,
    QuestionItem,
    QuestionStep,
    QuestionType,
    SyntheseDto,
} from '../Models/Questions/QuestionDto';
import { MapContextState } from '../Plugins/FloorPlan/Context/MapContext';
import { IMapItem } from '../Plugins/FloorPlan/Models/IMapItem';
import { IOpening } from '../Plugins/FloorPlan/Models/IOpening';
import { IRoom, RoomType, StairsSteps, StairsStepType } from '../Plugins/FloorPlan/Models/IRoom';
import { IRoomItem } from '../Plugins/FloorPlan/Models/IRoomItem';
import { ConditionHelper } from './ConditionHelper';
import { QuestionContext, QuestionContextHelper } from './QuestionContextHelper';
import { QuoteQuestionUtils } from './QuoteQuestionUtils';

export type SyntheseMapItem<T extends IMapItem> = { item?: T; formattedSyntheses: Array<string>; }

export type GetSynthesesParameters = { map: Partial<MapContextState>; quoteState: QuoteState }

export interface QuestionsSyntheses {
    synthesesGeneral: string[];
    synthesesByRoom: SyntheseMapItem<IRoom>[];
    synthesesByOpening: SyntheseMapItem<IOpening>[];
    synthesesByRoomItem: SyntheseMapItem<IRoomItem>[];
}

export class MatchHelper {
    public static UUIDV4_Regex = new RegExp(/[0-9A-F]{8}-[0-9A-F]{4}-4[0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12}/gi);
    public static SyntheseMapItemProperty_Regex = new RegExp(/question_floorplan_(\w+)/g);
}

export class SynthesesHelper {

    public static getSyntheses = ({ quoteState, map }: GetSynthesesParameters) => {
        const { stepHabitation, stepServices, stepSynthese } = quoteState;

        const { rooms, openings, roomItems } = MapHelper.getRoomAllElements(map.rooms);
        const synthesesGeneral: string[] = [
            ...this.toFormattedSyntheses(stepHabitation?.questions),
            ...this.toFormattedSyntheses(stepServices?.questions),
            ...this.toFormattedSyntheses(stepSynthese?.questions)
        ];
        const synthesesByRoom = this.toSyntheseMapItems<IRoom>(rooms);
        const synthesesByOpening = this.toSyntheseMapItems<IOpening>(openings);
        const synthesesByRoomItem = this.toSyntheseMapItems<IRoomItem>(roomItems);

        return { synthesesGeneral, synthesesByRoom, synthesesByOpening, synthesesByRoomItem };
    };

    private static toSyntheseMapItems = <T extends IMapItem>(
        mapItems: Array<T> = []
    ): Array<SyntheseMapItem<T>> => {
        return mapItems.map((item) => {
            const formattedSyntheses: string[] = []

            const stairs = item as Partial<IRoom>;
            if (stairs.type === RoomType.Stairs) {
                formattedSyntheses.push(...this.toStairsStepSyntheses(stairs.stairsSteps));
            }

            const questions = [
                ...(item.questionsTva?.questions || []),
                ...(item.questionsProduit?.questions || []),
                ...(item.questionsPose?.questions || []),
                ...(item.questionsSupport?.questions || []),
                ...(item.questionsPreparation?.questions || []),
                ...(item.questionsFinition?.questions || []),
            ];
            formattedSyntheses.push(...this.toFormattedSyntheses(questions));
            return { item, formattedSyntheses };
        });
    };

    private static toFormattedSyntheses = (questions: QuestionItem[] = []): Array<string> => {
        let formattedSyntheses: string[] = [];
        questions
            .filter((question) => [QuestionStep.StepHabitation, QuestionStep.StepServices, QuestionStep.StepSynthese].includes(question.related_step) || question?.showQuestion)
            .forEach((question: QuestionItem): void => {
                question.synthese_list?.forEach((synthese: SyntheseDto): void => {
                    if (ConditionHelper.isConditionEvaluateToTrue({ rawLogic: synthese.visibility_condition?.json_logic, rawContext: question.context })) {
                        let templateSynthese: string = synthese.template;
                        let formattedSynthese: string | undefined;

                        const tags = templateSynthese.match(MatchHelper.UUIDV4_Regex);
                        if (tags) {
                            for (const tag of tags) {
                                const currentSyntheseQuestion = questions.find((x) => x.id === tag);
                                if (currentSyntheseQuestion) {
                                    formattedSynthese = this.transformSyntheseTemplate(
                                        currentSyntheseQuestion,
                                        question.context,
                                        questions,
                                        templateSynthese
                                    );
                                }
                            }
                        } else {
                            const mapItemTags = Array.from(
                                templateSynthese.matchAll(MatchHelper.SyntheseMapItemProperty_Regex),
                                (m) => ({ baseTag: m[0], shortTag: m[1] })
                            );
                            if (mapItemTags) {
                                formattedSynthese = this.transformMapItemPropertiesAnswers(
                                    question.context,
                                    templateSynthese,
                                    mapItemTags
                                );
                            }
                        }

                        if (formattedSynthese !== undefined) {
                            formattedSyntheses.push(formattedSynthese);
                        }
                    }
                });
            });
        return formattedSyntheses;
    };

    private static transformSyntheseTemplate(
        syntheseQuestion: QuestionItem,
        context: QuestionContext = {},
        questionSyntheses: Array<QuestionItem>,
        formattedSynthese: string
    ): string | undefined {
        const syntheseQuestionId: string = QuoteQuestionUtils.toQuestionId(syntheseQuestion.id);
        let formattedSyntheseResult: string | undefined;

        const isInContext = (key: string, context: QuestionContext): boolean => Boolean(context[key]);

        const transform = (template: string, key: string, value: string): string => {
            if (template.includes(key)) {
                return template.replaceAll(key, value);
            }
            return template;
        };

        switch (syntheseQuestion.type) {
            case QuestionType.SingleChoice:
                if (syntheseQuestion.single_choice_properties) {
                    if (formattedSynthese.includes(syntheseQuestionId)) {
                        const syntheseQuestionOption: string = QuestionContextHelper.toQuestionOption(
                            syntheseQuestion.id
                        );
                        if (isInContext(syntheseQuestionOption, context)) {
                            const questionOptionValue: string = context[syntheseQuestionOption];
                            //* search option label
                            const currentSyntheseQuestion = questionSyntheses.find((x) => x.id === syntheseQuestion.id);
                            currentSyntheseQuestion?.single_choice_properties!.option_list.forEach(
                                (option: OptionDto) => {
                                    const optionIdKey = QuestionContextHelper.toOptionId(option.id);
                                    if (questionOptionValue === optionIdKey) {
                                        formattedSyntheseResult = transform(
                                            formattedSynthese,
                                            syntheseQuestionId,
                                            option.label
                                        );
                                    }
                                }
                            );
                        }
                    }
                }
                break;

            case QuestionType.Produit:
                if (formattedSynthese.includes(syntheseQuestionId)) {
                    if (isInContext(syntheseQuestionId, context)) {
                        const produit = syntheseQuestion.produitValue;
                        const libelle = produit?.attributs?.find(attr => attr.name.endsWith('LibelleWeb'))?.value;
                        const shortCode = ProductHelper.toCodeShort(produit?.code);
                        const description = `${libelle} (${shortCode})`;
                        formattedSyntheseResult = transform(formattedSynthese, syntheseQuestionId, description);
                    }
                }
                break;

            case QuestionType.MultipleChoicesProduit:
                if (formattedSynthese.includes(syntheseQuestionId)) {
                    if (isInContext(syntheseQuestionId, context)) {
                        const formatDescription = (produit: ProduitDto, count: number): string => {
                            const libelle = produit.attributs?.find(attr => attr.name.endsWith('LibelleWeb'))?.value;
                            const shortCode = ProductHelper.toCodeShort(produit.code);
                            return `${libelle} (${shortCode} x${count})`;
                        }
                        const produitByCount = Object.values(syntheseQuestion.multiChoicesProduitCartValues || {});
                        const descriptionFormatted = produitByCount.map(item => formatDescription(item.item, item.count)).join(' , ')
                        formattedSyntheseResult = transform(formattedSynthese, syntheseQuestionId, descriptionFormatted);
                    }
                }
                break;

            case QuestionType.MultipleChoicesPrestation:
                if (formattedSynthese.includes(syntheseQuestionId)) {
                    if (isInContext(syntheseQuestionId, context)) {
                        const formatDescription = (prestation: PrestationDto, count: number): string => {
                            const libelle = prestation.libelle;
                            return `${libelle} (${prestation.code} x${count})`;
                        }
                        const prestationByCount = Object.values(syntheseQuestion.multiChoicesPrestationCartValues || {});
                        const descriptionFormatted = prestationByCount.map(item => formatDescription(item.item, item.count)).join(' , ')
                        formattedSyntheseResult = transform(formattedSynthese, syntheseQuestionId, descriptionFormatted);
                    }
                }
                break;

            case QuestionType.Number:
                if (formattedSynthese.includes(syntheseQuestionId)) {
                    const questionValueKey: string = QuestionContextHelper.toQuestionValue(syntheseQuestion.id);
                    if (isInContext(questionValueKey, context)) {
                        const number = context[questionValueKey];
                        formattedSyntheseResult = transform(formattedSynthese, syntheseQuestionId, number);
                    }
                }
                break;

            case QuestionType.MultipleChoices:
                if (syntheseQuestion.multiple_choices_properties) {
                    if (formattedSynthese.includes(syntheseQuestionId)) {
                        const questionOptionKey: string = QuestionContextHelper.toQuestionOption(syntheseQuestion.id);

                        if (isInContext(questionOptionKey, context)) {
                            const questionOptionsValues: Array<string> = context[questionOptionKey];
                            const selectedOptionsLabels: Array<string> =
                                syntheseQuestion.multiple_choices_properties.option_list
                                    .filter((x) =>
                                        questionOptionsValues.includes(QuestionContextHelper.toOptionId(x.id))
                                    )
                                    .map((x) => x.label);

                            formattedSyntheseResult = transform(
                                formattedSynthese,
                                syntheseQuestionId,
                                selectedOptionsLabels.join(', ')
                            );
                        }
                    }
                }
                break;

            case QuestionType.Comment:
                if (formattedSynthese.includes(syntheseQuestionId)) {
                    const questionValueKey: string = QuestionContextHelper.toQuestionValue(syntheseQuestion.id);
                    if (isInContext(questionValueKey, context)) {
                        const comment = context[questionValueKey];
                        formattedSyntheseResult = transform(formattedSynthese, syntheseQuestionId, comment);
                    }
                }
                break;

            case QuestionType.ZipCode:
                const zipCode = syntheseQuestion.zipCodeValue?.zipcode;
                formattedSyntheseResult = zipCode
                    ? transform(formattedSynthese, syntheseQuestionId, zipCode)
                    : formattedSynthese;
                break;
        }
        return formattedSyntheseResult;
    }

    private static transformMapItemPropertiesAnswers(
        context: QuestionContext,
        formattedSynthese: string,
        mapItemPropertiesTags: Array<{ baseTag: string; shortTag: string; }>
    ): string | undefined {
        const defaultValueIfNeeded = (value: string): string => {
            return value === QuestionContextHelper.NOT_SET ? '-' : value;
        };
        for (const tag of mapItemPropertiesTags) {
            if (context?.[tag.shortTag] !== undefined && context?.[tag.shortTag] !== '') {
                formattedSynthese = formattedSynthese.replace(tag.baseTag, defaultValueIfNeeded(context[tag.shortTag]));
            } else if (
                //@ts-ignore
                context?.contournements?.[tag.shortTag] !== undefined &&
                //@ts-ignore
                context?.contournements?.[tag.shortTag] !== ''
            ) {
                //@ts-ignore
                formattedSynthese = formattedSynthese.replace(tag.baseTag, context?.contournements[tag.shortTag]);
            } else {
                return undefined;
            }
        }
        return formattedSynthese;
    }

    private static toStairsStepSyntheses = (stairsSteps: Array<StairsSteps> = []): Array<string> => {
        return stairsSteps.length === 0
            ? []
            : Enumerable.from(stairsSteps)
                .groupBy(
                    step => `${step.type}-${step.marches}-${step.giron}`,
                    step => step,
                    (key, group) => {
                        const first: StairsSteps = group.first();
                        const quantity = group.sum(x => x.quantity);

                        const getLabel = (type: StairsStepType, quantity: number): string => {
                            const isPlural = quantity > 1;
                            switch (type) {
                                case StairsStepType.MarcheDroite:
                                    return isPlural ? 'marches droites' : 'marche droite';
                                case StairsStepType.MarcheTournante:
                                    return isPlural ? 'marches tournantes' : 'marche tournante';
                                case StairsStepType.MarcheArrondie:
                                    return isPlural ? 'marches arrondies' : 'marche arrondie';
                                case StairsStepType.Palier:
                                    return isPlural ? 'paliers' : 'palier';
                                default:
                                    return type;
                            }
                        };
                        const label = getLabel(first.type, quantity);
                        return `${quantity} ${label} (L.${first.marches}cm x l.${first.giron}cm)`;
                    }
                )
                .toArray();
    }
}
